import type {
  ActivationsPage,
  AuditLogsPage,
  CustomersPage,
  DashboardData,
  EntitlementDetail,
  EntitlementsPage,
  Job,
  JobsPage,
  PilotMember,
  PilotMembersPage,
} from './types'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api'
let adminAccessToken = ''

type EntitlementPatch = {
  seat_count?: number
  seat_limit_enforced?: boolean
  updates_until?: string
  channel?: string
  status?: string
}

export type EntitlementSearch = {
  query?: string
  product?: string
  channel?: string
  status?: string
  limit?: number
  offset?: number
}

export type PageSearch = {
  query?: string
  limit?: number
  offset?: number
}

export type ActivationSearch = PageSearch & {
  product?: string
  status?: string
}

export type JobSearch = {
  status?: string
  jobType?: string
  limit?: number
  offset?: number
}

export type AuditSearch = PageSearch & {
  action?: string
  outcome?: string
}

export type ReleaseCreate = {
  product_slug: string
  version: string
  channel: string
  release_notes: string
  mandatory: boolean
}

export type ArtifactCreate = {
  filename: string
  content_type: string
  host: string
  host_version_min: number
  host_version_max: number
  platform: string
  architecture: string
  ruby_abi?: string
  sha256: string
  size_bytes: number
}

type ArtifactUpload = {
  artifact: {
    id: string
    release_id: string
    object_key: string
    filename: string
    content_type: string
    sha256: string
    size_bytes: number
    status: string
  }
  upload: {
    url: string
    method: string
    headers: Array<{ name: string; value: string }>
    expires_at: string
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(adminAccessToken ? { Authorization: `Bearer ${adminAccessToken}` } : {}),
      ...init?.headers,
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.error?.message ?? `Request failed (${response.status})`)
  }
  return response.json() as Promise<T>
}

export const api = {
  authenticate: async (bootstrapToken: string) => {
    const response = await fetch(`${API_BASE}/v1/admin/auth/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ bootstrap_token: bootstrapToken }),
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.error?.message ?? 'Admin sign-in failed')
    }
    const session = (await response.json()) as {
      access_token: string
      expires_at: string
      email: string
      role: string
    }
    adminAccessToken = session.access_token
    return session
  },
  dashboard: () => request<DashboardData>('/v1/admin/dashboard'),
  customers: (search: PageSearch = {}) => {
    const query = new URLSearchParams({
      limit: String(search.limit ?? 25),
      offset: String(search.offset ?? 0),
    })
    if (search.query) query.set('q', search.query)
    return request<CustomersPage>(`/v1/admin/customers?${query}`)
  },
  activations: (search: ActivationSearch = {}) => {
    const query = new URLSearchParams({
      limit: String(search.limit ?? 25),
      offset: String(search.offset ?? 0),
    })
    if (search.query) query.set('q', search.query)
    if (search.product) query.set('product', search.product)
    if (search.status) query.set('status', search.status)
    return request<ActivationsPage>(`/v1/admin/activations?${query}`)
  },
  jobs: (search: JobSearch = {}) => {
    const query = new URLSearchParams({
      limit: String(search.limit ?? 50),
      offset: String(search.offset ?? 0),
    })
    if (search.status) query.set('status', search.status)
    if (search.jobType) query.set('job_type', search.jobType)
    return request<JobsPage>(`/v1/admin/jobs?${query}`)
  },
  retryJob: (jobId: string) =>
    request<Job>(`/v1/admin/jobs/${jobId}/retry`, { method: 'POST' }),
  auditLogs: (search: AuditSearch = {}) => {
    const query = new URLSearchParams({
      limit: String(search.limit ?? 50),
      offset: String(search.offset ?? 0),
    })
    if (search.query) query.set('q', search.query)
    if (search.action) query.set('action', search.action)
    if (search.outcome) query.set('outcome', search.outcome)
    return request<AuditLogsPage>(`/v1/admin/audit-logs?${query}`)
  },
  pilotMembers: (query = '') => {
    const search = new URLSearchParams({ limit: '100', offset: '0' })
    if (query) search.set('q', query)
    return request<PilotMembersPage>(
      `/v1/admin/account/pilot-members?${search}`,
    )
  },
  enrollPilotMember: (email: string, note: string) =>
    request<PilotMember>('/v1/admin/account/pilot-members', {
      method: 'POST',
      body: JSON.stringify({ email, note: note || null }),
    }),
  removePilotMember: (userId: string) =>
    request<{ success: boolean; sessions_revoked: number }>(
      `/v1/admin/account/pilot-members/${userId}`,
      { method: 'DELETE' },
    ),
  entitlements: (search: EntitlementSearch = {}) => {
    const query = new URLSearchParams()
    if (search.query) query.set('q', search.query)
    if (search.product) query.set('product', search.product)
    if (search.channel) query.set('channel', search.channel)
    if (search.status) query.set('status', search.status)
    query.set('limit', String(search.limit ?? 25))
    query.set('offset', String(search.offset ?? 0))
    return request<EntitlementsPage>(`/v1/admin/entitlements?${query}`)
  },
  entitlement: (entitlementId: string) =>
    request<EntitlementDetail>(`/v1/admin/entitlements/${entitlementId}`),
  deactivate: (activationId: string) =>
    request<{ success: boolean }>(`/v1/admin/activations/${activationId}`, {
      method: 'DELETE',
    }),
  revokeActivation: (activationId: string, reason: string) =>
    request<{ success: boolean }>(
      `/v1/admin/activations/${activationId}/revoke`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    ),
  patchEntitlement: (entitlementId: string, patch: EntitlementPatch) =>
    request<{ success: boolean }>(`/v1/admin/entitlements/${entitlementId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  createRelease: (release: ReleaseCreate) =>
    request<{
      id: string
      product_slug: string
      version: string
      channel: string
      status: string
    }>('/v1/admin/releases', {
      method: 'POST',
      body: JSON.stringify(release),
    }),
  createArtifact: (releaseId: string, artifact: ArtifactCreate) =>
    request<ArtifactUpload>(`/v1/admin/releases/${releaseId}/artifacts`, {
      method: 'POST',
      body: JSON.stringify(artifact),
    }),
  uploadArtifact: async (upload: ArtifactUpload['upload'], file: File) => {
    const response = await fetch(upload.url, {
      method: upload.method,
      headers: Object.fromEntries(
        upload.headers.map(({ name, value }) => [name, value]),
      ),
      body: file,
    })
    if (!response.ok) {
      const body = await response.json().catch(() => null)
      throw new Error(body?.error?.message ?? `Artifact upload failed (${response.status})`)
    }
  },
  finalizeArtifact: (artifactId: string) =>
    request<{ success: boolean; signature: string }>(
      `/v1/admin/artifacts/${artifactId}/finalize`,
      { method: 'POST' },
    ),
  publishRelease: (releaseId: string) =>
    request<{ success: boolean }>(`/v1/admin/releases/${releaseId}/publish`, {
      method: 'POST',
    }),
  archiveRelease: (releaseId: string) =>
    request<{ success: boolean; artifacts_deleted: number }>(
      `/v1/admin/releases/${releaseId}/archive`,
      { method: 'POST' },
    ),
}
